<script>
import useVuelidate from '@vuelidate/core';
import {required} from '@vuelidate/validators';

/* COMPOSANTS */
import GaView from '@components/v2/layout/GaView';
import AkInputMoney from '@components/v2/input/AkInputMoney';
import AkInputPercentage from '@components/v2/input/AkInputPercentage';
import AkInputInteger from '@components/v2/input/AkInputInteger';
import AkButtonValidation from '@components/v2/general/AkButtonValidation.vue';
import AkDatatablePagination from '@components/v2/general/AkDatatablePagination.vue';
import AkDialog from '@components/general/AkDialog.vue';
import AkInputText from '@components/v2/input/AkInputText.vue';
import AkButtonAction from '@components/v2/general/AkButtonAction.vue';
import GaListView from '@components/v2/layout/GaListView.vue';
import AkDatatable from '@components/v2/general/AkDatatable.vue';
import InfosMandatForm from '@views/settings/InfosMandat/InfosMandatForm.vue';

/* MIXINS */
import utilsMixin from '@mixins/utilsMixin';
import messageMixin from '@mixins/messageMixin';

/* SERVICES */
import mandatSettingService from '@services/mandatSettingService';
import settingService from '@services/settingService';
import amountThresholdService from '@services/amountThresholdService';

export default {
  components: {
    GaListView,
    AkButtonAction,
    AkInputText,
    AkDialog,
    AkDatatablePagination,
    AkButtonValidation,
    AkInputPercentage,
    AkInputMoney,
    AkInputInteger,
    GaView,
    AkDatatable,
    InfosMandatForm,
  },
  mixins: [messageMixin, utilsMixin],
  setup() {
    return {v$: useVuelidate()};
  },
  head() {
    return {
      title: 'settings.infos_mandat',
    };
  },
  data() {
    return {
      loading: true,
      currentTab: 'INFOS_MANDAT',
      submitted: false,
      mandatSetting: {},
      selectedMandatSetting: {},
      mandatNumSetting: {},
      amountThresholdList: [],
      AmountThresholdItem: {},
      crg: {val: false},
      // Tri de l'historique des informations du mandat, plus récente en premier
      genericSort: [{field: 'dateUpdated', order: -1}],
    };
  },
  validations() {
    return {
      AmountThresholdItem: {
        label: {required},
        amountGa: {required},
      },
    };
  },
  mounted() {
    const promises = [];

    promises.push(
      mandatSettingService
        .findCurrent()
        .then((data) => {
          this.mandatSetting = data;
        })
        .catch((e) => {
          this.addError(this.msgForErr(this.$t, e));
        }),
    );
    promises.push(
      settingService.setting('MANDATE_NUM').then((data) => {
        this.mandatNumSetting = data;
      }),
    );
    promises.push(
      amountThresholdService.amountThresholds().then((data) => {
        this.amountThresholdList = data;
      }),
    );
    promises.push(
      settingService.setting('SEND_CRG').then((data) => {
        this.crg = data;
        this.crg.val = this.crg.val === 'true';
      }),
    );

    Promise.all(promises).then(() => {
      this.loading = false;
    });
  },
  computed: {
    previousMandatSettings() {
      return this.mandatSetting?.previousMandatSettings ?? [];
    },
    selectedMandatSettingTitle() {
      let title = 'Informations du mandat';
      if (this.selectedMandatSetting?.dateUpdated) {
        title += ` - ${this.formatDateIfNeeded(this.selectedMandatSetting.dateUpdated)}`;
      }
      return title;
    },
  },
  methods: {
    changeTab(tab) {
      this.currentTab = tab;
    },

    // UPDATE Informations Mandat
    update() {
      this.submitted = true;
      this.loading = true;

      mandatSettingService
        .update(this.mandatSetting)
        .then((data) => {
          this.success(this.$t('updated'));
          this.submitted = false;
          this.mandatSetting = data;
        })
        .catch((e) => {
          if (e.status === 412) {
            const error = e.response?.data?.error;
            if (error === 'NO_DATA_CHANGE') {
              this.addWarning(this.$t('mandat_setting.warning_no_data_changed'));
            } else {
              this.addError(error);
            }
          } else {
            this.addError(this.msgForErr(this.$t, e));
          }
        })
        .finally(() => {
          this.loading = false;
          this.$refs.updateButton.init();
        });
    },

    createAmountThreshold() {
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$error) {
        this.$refs.dialogCreateAmountThreshold.init();
        return;
      }

      this.loading = true;
      amountThresholdService
        .create(this.AmountThresholdItem)
        .then((data) => {
          this.amountThresholdList.push(data);
          this.item = {};
          this.success(this.$t('amountThreshold.added'));
        })
        .finally(() => {
          this.submitted = false;
          this.loading = false;
          this.$refs.dialogCreateAmountThreshold.hide();
        });
    },
    updateAmountThreshold() {
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$error) {
        this.$refs.dialogEditAmountThreshold.init();
        return;
      }

      this.loading = true;
      amountThresholdService
        .update(this.AmountThresholdItem)
        .then((data) => {
          this.replaceInListById(this.amountThresholdList, data);
          this.success(this.$t('amountThreshold.updated'));
        })
        .finally(() => {
          this.submitted = false;
          this.loading = false;
          this.$refs.dialogEditAmountThreshold.hide();
        });
    },
    openCreateAmountThresholdDialog() {
      this.AmountThresholdItem = {};
      this.$refs.dialogCreateAmountThreshold.show();
    },
    openEditAmountThresholdDialog(item) {
      this.AmountThresholdItem = {...item};
      this.$refs.dialogEditAmountThreshold.show();
    },
    reorder(event) {
      const item = this.amountThresholdList[event.dragIndex];
      this.loading = true;
      amountThresholdService.reorderPosition(item, event.dropIndex - event.dragIndex).then(() => {
        this.reorderInListByIdAndDelta(this.amountThresholdList, item.id, event.dropIndex - event.dragIndex);
        this.loading = false;
      });
    },

    // ROW CLICK
    rowClick(item) {
      this.selectedMandatSetting = {...item.data};
      this.$nextTick(() => {
        this.$refs.dialogInfosMandatDetails.show();
      });
    },
  },
};
</script>

<template>
  <GaView :title="$t('settings.infos_mandat')">
    <template #action>
      <AkButtonAction
        v-if="currentTab === 'AMOUNT_THRESHOLD'"
        :label="$t('add')"
        little
        @click="openCreateAmountThresholdDialog()" />
    </template>
    <template #content>
      <GaListView>
        <template #tabs>
          <div class="flex items-center">
            <span
              :class="[{active: currentTab === 'INFOS_MANDAT'}]"
              class="list-table-item"
              @click="changeTab('INFOS_MANDAT')"
              >{{ $t('settings.infos_mandat') }}</span
            >
            <span
              :class="[{active: currentTab === 'AMOUNT_THRESHOLD'}]"
              class="list-table-item"
              @click="changeTab('AMOUNT_THRESHOLD')"
              >{{ $t('mandat.amountThreshold') }}</span
            >
          </div>
        </template>

        <template #content>
          <template v-if="currentTab === 'INFOS_MANDAT'">
            <InfosMandatForm v-model="mandatSetting" :submitted="submitted" :loading="loading" />

            <AkDatatable
              :value="previousMandatSettings"
              :loading="false"
              v-model:multiSortMeta="this.genericSort"
              @row-click="rowClick($event)">
              <template #empty>
                {{ $t('mandat_setting.empty') }}
              </template>

              <Column
                field="dateUpdated"
                header-style="width: 100px;"
                :header="$t('mandat_setting.date_updated')"
                :sortable="true">
                <template #body="slotProps">
                  {{ displayDate(slotProps.data.dateUpdated) }}
                </template>
              </Column>

              <Column header-style="width: 50px;" header=" ">
                <template #body="slotProps">
                  <div class="flex justify-end items-center gap-2">
                    <span class="cursor-pointer" @click="rowClick(slotProps)">
                      <i class="ga-icon ga-external text-2xl text-grey-900" />
                    </span>
                  </div>
                </template>
              </Column>
            </AkDatatable>

            <div class="grid grid-cols-2 md:gap-4 gap-2 mt-4">
              <AkInputText v-model="mandatNumSetting.val" :disabled="true" :label="$t('mandat.num')" />
            </div>

            <div class="flex md:justify-end justify-center md:mt-4 mt-2">
              <AkButtonValidation ref="updateButton" :label="$t('update')" @action="this.update" />
            </div>
          </template>

          <template v-if="currentTab === 'AMOUNT_THRESHOLD'">
            <AkDatatablePagination
              :lazy="false"
              :loading="loading"
              :value="amountThresholdList"
              can-change-order
              class="mt-4"
              @row-reorder="reorder">
              <template #empty>
                {{ $t('stock.empty') }}
              </template>
              <Column :header="$t('amountThreshold.label')" field="label">
                <template #body="slotProps">
                  {{ slotProps.data.label }}
                </template>
              </Column>
              <Column :header="$t('amountThreshold.amountGa')" field="amountGa">
                <template #body="slotProps">
                  {{ formatCurrencyEUR(formatAmount(slotProps.data.amountGa)) }}
                </template>
              </Column>
              <Column :header="$t('amountThreshold.amountGav')" field="amountGav">
                <template #body="slotProps">
                  {{ formatCurrencyEUR(formatAmount(slotProps.data.amountGav ?? 0)) }}
                </template>
              </Column>
              <Column style="width: 100px">
                <template #body="slotProps">
                  <div class="flex justify-end gap-2">
                    <span class="md:hover:cursor-pointer" @click="openEditAmountThresholdDialog(slotProps.data)">
                      <i class="ga-icon ga-edit text-2xl text-grey-900" />
                    </span>
                  </div>
                </template>
              </Column>
            </AkDatatablePagination>
          </template>
        </template>
      </GaListView>
    </template>

    <template #extra>
      <AkDialog
        ref="dialogInfosMandatDetails"
        :title="selectedMandatSettingTitle"
        width="1200px"
        :show-validate-button="false"
        :show-cancel-button="false">
        <InfosMandatForm
          v-if="selectedMandatSetting"
          v-model="selectedMandatSetting"
          :submitted="false"
          :loading="false"
          :disabled="true" />
      </AkDialog>

      <AkDialog
        ref="dialogCreateAmountThreshold"
        :title="$t('amountThreshold.add')"
        :validate-label="$t('add')"
        width="450px"
        @validate="createAmountThreshold()">
        <div class="flex flex-col gap-2">
          <AkInputText
            v-model="AmountThresholdItem.label"
            :label="$t('amountThreshold.label')"
            :validator="v$.AmountThresholdItem.label" />
          <AkInputMoney
            v-model="AmountThresholdItem.amountGa"
            :label="$t('amountThreshold.amountGa')"
            :validator="v$.AmountThresholdItem.amountGa" />
        </div>
      </AkDialog>
      <AkDialog
        ref="dialogEditAmountThreshold"
        :title="$t('amountThreshold.update')"
        :validate-label="$t('update')"
        width="450px"
        @validate="updateAmountThreshold()">
        <div class="flex flex-col gap-2">
          <AkInputText
            v-model="AmountThresholdItem.label"
            :label="$t('stock.label')"
            :validator="v$.AmountThresholdItem.label" />
        </div>
      </AkDialog>
    </template>
  </GaView>
</template>
